import { _ } from "../../polyglot/polyglot.js";
import { widget } from "@foretagsplatsen/widgetjs";
import basCategorizedListWidget from "./basCategorizedListWidget.js";
import jQuery from "jquery";

/**
 *
 * `keyFigurePickerContentWidget` represents the content of the popover used to pick
 * items in order to define a scope.
 *
 * It is composed of two buttons to toggle between names and numbers, and a
 * categorized list to display and pick the items.
 *
 * @param {{}} spec - Parameter object
 * @param {function} [spec.nameClicked]     - callback performed when the name button is clicked
 * @param {function} [spec.numbersClicked]  - callback performed when the numbers button is clicked
 * @param {*[]} spec.categories             - list of categories to display
 * @param {*[]} spec.items                  - list of currently picked items
 * @param {function} [checkIfEnabled] - Function checking if the item
 *   should be disabled. It takes a keyFigure item as parameter and
 *   returns an object containing an `isEnabled` boolean and
 *   `disabledTitle` to give information to the user on why the item
 *   is disabled
 * @param {{}} my - protected fields holder
 * @return {*}
 */
function keyFigurePickerContentWidget(spec, my) {
	spec ||= {};
	my ||= {};
	const that = widget(spec, my);

	const nameClicked = spec.nameClicked || function () {};
	const numberClicked = spec.numberClicked || function () {};
	let categories = spec.categories || [];
	const selectedItems = spec.items;
	const multipleSelection = spec.multipleSelection;
	const checkIfEnabled = spec.checkIfEnabled;

	let itemLabel = function (item) {
		return item.getName();
	};

	let itemSorter = function (a, b) {
		return a.getName() > b.getName()
			? 1
			: // eslint-disable-next-line sonarjs/no-nested-conditional -- don't want to change that now
				a.getName() < b.getName()
				? -1
				: 0;
	};

	let currentSelection = {};

	that.itemStatusChanged = my.events.createEvent();
	that.onItemClicked = my.events.createEvent();

	my.categorizedList = basCategorizedListWidget({
		categories,
		itemRenderer,
		selectedItems: multipleSelection ? selectedItems : [],
		categoryRenderer,
		itemSearchString: function (item) {
			return item.getName();
		},
		itemsSorter: itemSorter,
	});

	that.updateSelection = function () {
		if (multipleSelection) {
			return;
		}

		selectedItems.forEach(selectKeyFigure);
	};

	that.showCategory = function (category) {
		if (category === "all") {
			my.categorizedList.showAll();
		} else {
			my.categorizedList.showCategory(category);
		}
	};

	that.resetSearch = function () {
		my.categorizedList.resetSearch();
	};

	that.setCategories = function (newCategories) {
		categories = newCategories;
		my.categorizedList.setCategories(newCategories);
		that.update();
	};

	that.applySearchFrom = function (searchField) {
		my.categorizedList.applySearchFrom(searchField);
	};

	that.getSelectedItems = function () {
		return getSelectedItems(categories);
	};

	that.selectKeyFigure = function (keyFigure) {
		selectKeyFigure(keyFigure);
		my.categorizedList.update();
		that.itemStatusChanged.trigger(currentSelection, true);
	};

	that.selectAll = function () {
		my.categorizedList.getRenderedItems().forEach((item) => {
			selectKeyFigure(item);
		});

		my.categorizedList.update();
		that.itemStatusChanged.trigger(currentSelection, true);
	};

	that.deselectAll = function () {
		const old = currentSelection;
		currentSelection = {};
		my.categorizedList.update();
		that.itemStatusChanged.trigger(old, false);
	};

	that.isSelectionEmpty = function () {
		const keys = Object.keys(currentSelection);
		return keys.every((key) => {
			if (!currentSelection[key]) {
				return true;
			}
			const ids = Object.keys(currentSelection[key]);
			return ids.every((id) => !currentSelection[key][id]);
		});
	};

	that.renderContentOn = function (html) {
		if (multipleSelection) {
			currentSelection = {};
		}

		html.div(
			{ klass: "categorized-list", "data-test": "key-figures-picker" },
			html.div(
				{ class: "btn-group radio-group pull-right" },
				html
					.a(
						{
							class: "btn btn-name btn-xs btn-admin-default active",
						},
						_("Name"),
					)
					.click(() => {
						jQuery(".btn-numbers").removeClass("active");
						jQuery(".btn-name").addClass("active");
						my.nameClicked();
					}),
				html
					.a(
						{
							class: "btn btn-xs btn-numbers btn-admin-default",
							"data-test": "bas-id",
						},
						_("BAS ID"),
					)
					.click(() => {
						jQuery(".btn-name").removeClass("active");
						jQuery(".btn-numbers").addClass("active");
						my.numberClicked();
					}),
			),
			html.div({ klass: "keyfigure-list" }, my.categorizedList),
		);
	};

	my.numberClicked = function () {
		itemLabel = function (item) {
			if (item.getBasId()) {
				return `${item.getBasId()} - ${item.getName()}`;
			}
			return item.getName();
		};

		itemSorter = function (a, b) {
			if (!b.getBasId()) {
				return -1;
			}
			if (!a.getBasId()) {
				return 1;
			}
			return a.getBasId() > b.getBasId()
				? 1
				: // eslint-disable-next-line sonarjs/no-nested-conditional -- don't want to change that now
					a.getBasId() < b.getBasId()
					? -1
					: 0;
		};

		my.categorizedList.update();
		numberClicked();
	};

	my.nameClicked = function () {
		itemLabel = function (item) {
			return item.getName();
		};

		itemSorter = function (a, b) {
			return a.getName() > b.getName()
				? 1
				: // eslint-disable-next-line sonarjs/no-nested-conditional -- don't want to change that now
					a.getName() < b.getName()
					? -1
					: 0;
		};

		my.categorizedList.update();
		nameClicked();
	};

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	function getSelectedItems(categories) {
		return categories.reduce((acc, category) => {
			const keyFigures = category
				.getItems()
				.filter((keyFigure) => isSelected(keyFigure, category));
			return acc.concat(keyFigures);
		}, []);
	}

	function isSelected(keyFigure, category) {
		const group = category.getName().toString();
		return (
			currentSelection &&
			currentSelection[group] &&
			currentSelection[group][keyFigure.getId()]
		);
	}

	function selectKeyFigure(keyFigure) {
		const group = keyFigure.getGroup().toString();

		currentSelection[group] ||= {};

		currentSelection[group][keyFigure.getId()] = true;
	}

	function toggleKeyFigure(keyFigure, category) {
		const group = category.getName().toString();

		if (currentSelection[group]) {
			currentSelection[group][keyFigure.getId()] =
				!currentSelection[group][keyFigure.getId()];
		} else {
			currentSelection[group] = {};
			currentSelection[group][keyFigure.getId()] = true;
		}
	}

	function itemRenderer(item, category) {
		if (multipleSelection) {
			return multipleSelectionItemRenderer(item, category);
		}
		return singleSelectionItemRenderer(item, category);
	}

	function singleSelectionItemRenderer(item, category) {
		return function (html) {
			const name = category.getName();
			const checked = isSelected(item, category);
			const { isEnabled, disabledTitle } = checkIfEnabled(item);
			let input;

			html.div(
				{ class: "radio" },
				html.label(
					{
						klass: { disabled: !isEnabled },
						title: `${isEnabled ? "" : disabledTitle}`,
					},
					(html) => {
						input = html.input({
							// `id` is mandatory, otherwise the wrong input is fetch
							// We think it might be an optimisation issue in jQuery
							// Nico & Ben -- 2016-03-02
							id: `${name}-${item.getIdentifier()}`,
							name: "keyFigureSelection",
							value: item.getIdentifier(),
							klass: "check-item",
							disabled: !isEnabled,
							title: `${isEnabled ? "" : disabledTitle}`,
							type: "radio",
						});
					},
					itemLabel(item),
				),
			);

			if (checked && isEnabled) {
				input.attr({ checked: true });
			}

			input.click(() => {
				currentSelection = {};
				selectKeyFigure(item);
				that.itemStatusChanged.trigger({ item: true }, true);
				that.onItemClicked.trigger(item);
			});
		};
	}

	function multipleSelectionItemRenderer(item, category) {
		return function (html) {
			const name = category.getName();
			const checked = isSelected(item, category);
			let input;

			html.div(
				{ class: "checkbox" },
				html.label((html) => {
					input = html.input({
						// `id` is mandatory, otherwise the wrong input is fetch
						// We think it might be an optimisation issue in jQuery
						// Nico & Ben -- 2016-03-02
						id: `${name}-${item.getIdentifier()}`,
						value: item.getIdentifier(),
						klass: "check-item",
						type: "checkbox",
					});
				}, itemLabel(item)),
			);

			if (checked) {
				input.attr({ checked: true });
			}

			input.click(() => {
				toggleKeyFigure(item, category);
				that.itemStatusChanged.trigger({ item: true }, true);
				that.onItemClicked.trigger(item);
			});
		};
	}

	function categoryRenderer(category) {
		return function (html) {
			html.h3(category.getName());
		};
	}

	return that;
}

export default keyFigurePickerContentWidget;
